import * as React from 'react';
import {Link as RouterLink} from 'react-router-dom';

import {Routing} from 'nutshell-core/routing';

import type {Tab} from 'shells/control-bar/page-control-bar';
import {AiSparkleNewIcon} from 'shells/icon/svg/ai-sparkle-new-icon';
import {BookRegularIcon} from 'shells/icon/svg/book-regular-icon';
import {ChatbotIcon} from 'shells/icon/svg/chatbot-icon';
import {ChatbotRegularIcon} from 'shells/icon/svg/chatbot-regular-icon';
import {CogRegularIcon} from 'shells/icon/svg/cog-regular-icon';
import {CursorRegularIcon} from 'shells/icon/svg/cursor-regular-icon';
// @ts-expect-error
import {ExternalLinkIcon} from 'shells/icon/svg/external-link-icon';
import {PaletteRegularIcon} from 'shells/icon/svg/palette-regular-icon';
import {Link} from 'shells/link/link';
import type {StatusBadgeProps} from 'shells/status-badge/status-badge';
import {Body} from 'shells/typography/body';

import type {
    ChatAssignmentStrategyEnum,
    ChatIconEnum,
    DocumentEffectiveStatusEnum,
    ChatbotIconEnum,
    DocumentSourceTypeEnum,
    ActionBarActionEnum,
} from 'nutshell-graphql-types';
import type {GetContextDocuments_contextDocuments as ContextDocument} from 'nutshell-graphql-types';

import {ActionsOptions} from './actions-options/actions-options';
import {BrandingOptions} from './branding-options/branding-options';
import {ChatBehaviorOptions} from './chat-behavior-options/chat-behavior-options';
import {EngagementBarOptions} from './engagement-bar-options/engagement-bar-options';

import {DEFAULT_ICONS, KnowledgeOptions} from '.';

import styles from './settings.module.css';

export const getTabOptionsM2 = (
    hasNoContent: boolean,
    refetchSettings: () => Promise<any>
): Tab[] => {
    const tabs = [
        {
            id: 'engagement-bar',
            label: 'Engagement bar',
            href: '#',
            component: <EngagementBarOptions hasNoKnowledgeContent={hasNoContent} />,
            icon: CursorRegularIcon,
        },
        {
            id: 'actions',
            label: 'Actions',
            href: '#',
            component: <ActionsOptions />,
            icon: CogRegularIcon,
        },
        {
            id: 'branding',
            label: 'Branding',
            href: '#',
            component: <BrandingOptions />,
            icon: PaletteRegularIcon,
        },
        {
            id: 'knowledge',
            label: 'Knowledge',
            href: '#',
            // @ts-expect-error
            component: <KnowledgeOptions refetchSettings={refetchSettings} />,
            badgeColor: hasNoContent ? 'orange' : undefined,
            icon: BookRegularIcon,
        },
        {
            id: 'chatbot-behavior',
            label: 'Chatbot behavior',
            href: '#',
            component: <ChatBehaviorOptions />,
            icon: ChatbotRegularIcon,
        },
    ];

    // @ts-expect-error
    return tabs;
};

export const getImagePath = (value: ChatAssignmentStrategyEnum) => {
    switch (value) {
        case 'ROUND_ROBIN':
            return '/include/images/chat/round-robin.png';
        case 'SPECIFIC_USER':
            return '/include/images/chat/specific-user.png';
        case 'NOBODY':
            return '/include/images/chat/nobody.png';
        default:
            return '/include/images/chat/round-robin.png';
    }
};

/*
 * Returns the chat widget icon based on the icon type and URL.
 * If the icon type is not found in the DEFAULT_ICONS object, it will return an image with the given URL.
 */
export function getChatWidgetIcon(iconType: ChatIconEnum, iconUrl?: string | null) {
    const icon = Object.keys(DEFAULT_ICONS).includes(iconType) ? (
        DEFAULT_ICONS[iconType]
    ) : (
        // @ts-expect-error
        <img src={iconUrl} className={styles['icon-image']} />
    );

    return icon;
}

export const getChatbotIcon = (
    icon?: ChatbotIconEnum | null
): React.ComponentType<any> | null | undefined => {
    switch (icon) {
        case 'CHATBOT':
            return ChatbotIcon;
        case 'SPARKLE':
            return AiSparkleNewIcon;
        case 'NONE':
        default:
            return null;
    }
};

export function buildOpenChatAssigneeUrl(userId: string) {
    const assigneeParams = {
        filter: {
            owner: {
                anyAll: 'any',
                data: [{data: userId}],
            },
        },
    } as const;
    const params = Routing.param(assigneeParams);

    return `/inbox/chat/open?${params}`;
}

export function buildNeedsReplyAssigneeUrl(userId: string) {
    const assigneeParams = {
        filter: {
            owner: {
                anyAll: 'any',
                data: [{data: userId}],
            },
        },
    } as const;
    const params = Routing.param(assigneeParams);

    return `/inbox/chat/waiting?${params}`;
}

export const getStatusBadgeInfo = (isChatEnabled: boolean): StatusBadgeProps => {
    return isChatEnabled
        ? {level: 'success', text: 'Enabled'}
        : {level: 'info--grey', text: 'Disabled'};
};

export const getStatusBadge = (
    effectiveStatus: DocumentEffectiveStatusEnum,
    sourceType: DocumentSourceTypeEnum
): StatusBadgeProps => {
    switch (effectiveStatus) {
        case 'DISABLED':
            return {level: 'info--grey', text: 'Disabled'};
        case 'PENDING':
            return {level: 'warning', text: sourceType === 'PDF' ? 'Processing…' : 'Crawling…'};
        case 'SUCCESS':
            return {level: 'success', text: 'Active'};
        case 'FAILED':
            return {level: 'alert', text: 'Couldn’t load'};
        case 'UNKNOWN':
        default:
            return {level: 'info--grey', text: 'Unknown'};
    }
};

export const getContentPreviewSubheaderText = (document: ContextDocument): React.ReactElement => {
    let text = '';
    if (document.sourceType === 'WEB_PAGE') {
        text = document.url ? document.url.fullyQualifiedUrl : 'Unknown';
    } else {
        text = document.name || 'the document';
    }

    return (
        <Body size='small' color='light'>
            This is a preview of{' '}
            <Link
                isBold={true}
                href={document.url ? document.url.fullyQualifiedUrl : ''}
                newTab={true}
                size='small'
            >
                {text}
            </Link>
        </Body>
    );
};

export const getExplanationForStrategy = (strategy: ChatAssignmentStrategyEnum): string => {
    switch (strategy) {
        case 'ROUND_ROBIN':
            return 'Visitor assignment will be distributed evenly among your team based on their availability.';
        case 'SPECIFIC_USER':
            return 'All incoming chats will be assigned to a specific user. If that user is not accepting chats, the chat will not be assigned.';
        case 'NOBODY':
            return 'Visitors will be placed in a queue, allowing your team to assign themselves accordingly.';
        default:
            return '';
    }
};

export const getActionOptionRowConfigureLink = (
    actionId: ActionBarActionEnum
): React.ReactNode | null | undefined => {
    if (actionId === 'CHAT_ACTION') {
        return null;
    }

    return (
        <Link
            to='/inbox/settings/chat/actions'
            // @ts-expect-error
            as={RouterLink}
            variant='primary'
            icon={ExternalLinkIcon}
        >
            Configure
        </Link>
    );
};
